import type { TFunction } from "i18next";

/*
  Every date and time the app prints, in the app's locale.

  `mn` is the default locale, and no browser can be trusted with it: Chrome has
  no Mongolian date data and quietly answers `toLocaleDateString("mn")` in
  English ("Oct 9", "Thu", "10/9/2026"). So Mongolian is composed here from the
  `date.*` keys — year, month, day in that order, months by number — and English
  goes through Intl, which every browser does well. The `date.*` keys exist in
  en.json too, for parity; the English path does not read the calendar ones.

  Relative phrases ("саяхан", "5 минутын өмнө", "өчигдөр") are keys in both
  locales.

  Every function takes the app's language (`i18n.language`), never the
  browser's, and gives "" for a date that does not parse (Intl would throw).
*/

type DateInput = Date | string | number;

const toDate = (input: DateInput) => (input instanceof Date ? input : new Date(input));
const invalid = (d: Date) => Number.isNaN(d.getTime());

/** Mongolian, or no language at all (the app's default is mn). */
const isMn = (lng?: string) => !lng || lng.split("-")[0] === "mn";

const WEEKDAYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"] as const;

const MIN = 60_000;
const HOUR = 60 * MIN;

/** Whole calendar days from `d` to `now`, in local time. */
const calendarDaysAgo = (d: Date, now: Date) => {
  const day = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  return Math.round((day(now) - day(d)) / (24 * HOUR));
};

/* ── Calendar ──────────────────────────────────────────────────────────────── */

/** "2026 оны 10-р сарын 9" · "October 9, 2026". Always with the year. */
export function formatLongDate(
  input: DateInput,
  t: TFunction,
  lng?: string,
  { month = "long" }: { month?: "long" | "short" } = {}
): string {
  const d = toDate(input);
  if (invalid(d)) return "";
  if (isMn(lng)) {
    return t("date.long", { year: d.getFullYear(), month: d.getMonth() + 1, day: d.getDate() });
  }
  return new Intl.DateTimeFormat(lng, { year: "numeric", month, day: "numeric" }).format(d);
}

/**
  "10-р сарын 9" in the current year, the long date in any other —
  "October 9" / "October 9, 2025".
*/
export function formatDate(
  input: DateInput,
  t: TFunction,
  lng?: string,
  { month = "long" }: { month?: "long" | "short" } = {}
): string {
  const d = toDate(input);
  if (invalid(d)) return "";
  if (d.getFullYear() !== new Date().getFullYear()) return formatLongDate(d, t, lng, { month });
  if (isMn(lng)) return t("date.short", { month: d.getMonth() + 1, day: d.getDate() });
  return new Intl.DateTimeFormat(lng, { month, day: "numeric" }).format(d);
}

/** "2026 оны 6-р сар" · "June 2026" (or "Jun 2026"). */
export function formatMonthYear(
  input: DateInput,
  t: TFunction,
  lng?: string,
  { month = "long" }: { month?: "long" | "short" } = {}
): string {
  const d = toDate(input);
  if (invalid(d)) return "";
  if (isMn(lng)) return t("date.monthYear", { year: d.getFullYear(), month: d.getMonth() + 1 });
  return new Intl.DateTimeFormat(lng, { year: "numeric", month }).format(d);
}

/** "Пүрэв" · "Thu". */
export function formatWeekday(input: DateInput, t: TFunction, lng?: string): string {
  const d = toDate(input);
  if (invalid(d)) return "";
  if (isMn(lng)) return t(`date.weekday.${WEEKDAYS[d.getDay()]}`);
  return new Intl.DateTimeFormat(lng, { weekday: "short" }).format(d);
}

/**
  The time of day: "14:05" in Mongolian (a 24-hour clock), "2:05 PM" in English.
  `hourCycle: "h23"` asks English for the same 24-hour "09:05".
*/
export function formatClock(
  input: DateInput,
  lng?: string,
  { hourCycle }: { hourCycle?: "h23" } = {}
): string {
  const d = toDate(input);
  if (invalid(d)) return "";
  if (isMn(lng)) {
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }
  return new Intl.DateTimeFormat(lng, {
    hour: hourCycle ? "2-digit" : "numeric",
    minute: "2-digit",
    hourCycle,
  }).format(d);
}

/* ── Relative ──────────────────────────────────────────────────────────────── */

/*
  List-row timestamp — compact: "now", "5m", "3h", "Yesterday", a weekday, then a
  date. Used by the Strings, Knots and Loops rows; the keys are `y.strings.time.*`
  in all of them, shared on purpose.
*/
export function formatTime(input: DateInput, t: TFunction, lng?: string): string {
  const d = toDate(input);
  if (invalid(d)) return "";
  const now = new Date();
  const diff = now.getTime() - d.getTime();
  const mins = Math.floor(diff / MIN);
  if (mins < 1) return t("y.strings.time.now");
  if (mins < 60) return t("y.strings.time.minutes", { count: mins });
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return t("y.strings.time.hours", { count: hrs });
  const days = Math.floor(hrs / 24);
  if (days === 1) return t("y.strings.time.yesterday");
  if (days < 7) return formatWeekday(d, t, lng);
  return formatDate(d, t, lng, { month: "short" });
}

/*
  Elapsed time, in words — "саяхан", "5 минутын өмнө", "3 цагийн өмнө",
  "өчигдөр", "4 өдрийн өмнө", and past a week the date itself. Used where a
  sentence-like stamp reads better than the list rows' compact one: the
  notifications, join requests and their detail.
*/
export function formatTimeAgo(input: DateInput, t: TFunction, lng?: string): string {
  const d = toDate(input);
  if (invalid(d)) return "";
  const now = new Date();
  const diff = Math.max(0, now.getTime() - d.getTime());
  const mins = Math.floor(diff / MIN);
  if (mins < 1) return t("date.justNow");
  if (mins < 60) return t("date.minutesAgo", { count: mins });
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return t("date.hoursAgo", { count: hrs });
  const days = calendarDaysAgo(d, now);
  if (days <= 1) return t("date.yesterday");
  if (days < 7) return t("date.daysAgo", { count: days });
  return formatDate(d, t, lng);
}
